import { useState } from 'react';
import { Lock, LogOut, Menu, X, ShieldCheck } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { logout as apiLogout } from '../../services/api';
import { getAdminAsSlug, clearAdminAsSlug } from '../../hooks/useTenant';
import { OnboardingProgress } from '../../hooks/useOnboardingProgress';

/**
 * Single source of truth for the artist dashboard navigation header.
 * All artist dashboard pages should use this component.
 * 
 * Paths are now flat (no /artist/:slug prefix) since tenant is resolved
 * from session or domain.
 */

interface NavItem {
  label: string;
  path: string;
  lockKey?: keyof OnboardingProgress;
  lockTooltip?: string;
}

interface ArtistDashboardNavProps {
  slug: string;
  currentPage: 'dashboard' | 'catalog' | 'growth' | 'fans' | 'transmissions' | 'pressings';
  onboardingProgress?: OnboardingProgress;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard' },
  { 
    label: 'Catalog', 
    path: '/catalog',
    lockKey: 'hasProfileImage',
    lockTooltip: 'Upload your profile image to unlock your Catalog',
  },
  { 
    label: 'Pressings', 
    path: '/pressings',
    lockKey: 'hasVerifiedTrack',
    lockTooltip: 'Upload a verified track to unlock your Pressings',
  },
  { 
    label: 'Growth', 
    path: '/growth',
    lockKey: 'hasPublishedVault',
    lockTooltip: 'Create and publish your first Pressing to enable Growth and Fans.',
  },
  { 
    label: 'Fans', 
    path: '/fans',
    lockKey: 'hasPublishedVault',
    lockTooltip: 'Create and publish your first Pressing to enable Growth and Fans.',
  },
  { 
    label: 'Transmissions', 
    path: '/transmissions',
    lockKey: 'hasAtLeastOneFan',
    lockTooltip: 'You need at least one registered Fan to unlock Transmissions',
  },
];

function NavTooltip({ text, visible }: { text: string; visible: boolean }) {
  if (!visible) return null;
  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-[100] pointer-events-none">
      <div className="bg-[#1A1A1A] border border-electric-cyan/30 rounded-lg px-3 py-2 shadow-lg shadow-black/40 w-[150px]">
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#1A1A1A] border-l border-t border-electric-cyan/30 rotate-45" />
        <p className="text-[11px] text-electric-cyan/90 font-mono leading-relaxed whitespace-normal text-center">
          {text}
        </p>
      </div>
    </div>
  );
}

export default function ArtistDashboardNav({ slug, currentPage, onboardingProgress }: ArtistDashboardNavProps) {
  const { user, token, logout } = useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const adminAsSlug = getAdminAsSlug();
  const isAdminViewing = user?.role === 'admin' && !!adminAsSlug;

  const isItemLocked = (item: NavItem): boolean => {
    if (!onboardingProgress || onboardingProgress.loading) return false;
    if (!item.lockKey) return false;
    return !(onboardingProgress[item.lockKey] as boolean);
  };

  const handleLogout = async () => {
    if (isAdminViewing) {
      // Admin viewing artist — go back to admin panel instead of logging out
      clearAdminAsSlug();
      window.location.href = '/admin';
      return;
    }
    try {
      await apiLogout(token!);
    } catch {}
    logout();
    window.location.href = '/';
  };

  const getHref = (item: NavItem) => {
    // When admin is viewing an artist, carry ?as= on every nav link
    if (adminAsSlug) {
      return `${item.path}?as=${encodeURIComponent(adminAsSlug)}`;
    }
    return item.path;
  };

  const getKey = (item: NavItem): ArtistDashboardNavProps['currentPage'] => {
    if (item.path === '/dashboard') return 'dashboard';
    return item.path.slice(1) as ArtistDashboardNavProps['currentPage'];
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/5 rounded-none">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <a href={adminAsSlug ? `/dashboard?as=${encodeURIComponent(adminAsSlug)}` : '/dashboard'} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img src="/fans-audio-logo.png" alt="fans.audio logo" className="w-7 h-7 object-contain" />
            <span className="font-mono font-bold text-lg">fans<span className="text-electric-cyan">.audio</span></span>
          </a>
        </div>

        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-1.5">
          {NAV_ITEMS.map((item, idx) => {
            const isActive = getKey(item) === currentPage;
            const locked = isItemLocked(item);
            return (
              <span key={item.label} className="flex items-center gap-1.5">
                {idx > 0 && <span className="text-electric-cyan/40 text-[10px]">♪</span>}
                {isActive ? (
                  <span className="text-xs text-white font-mono uppercase tracking-wider px-2 py-1">
                    {item.label}
                  </span>
                ) : locked ? (
                  <span
                    className="relative flex items-center cursor-not-allowed select-none"
                    onMouseEnter={() => setHoveredItem(item.label)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <span
                      className="text-xs text-text-muted font-mono uppercase tracking-wider px-2 py-1 flex items-center gap-1"
                      style={{ opacity: 0.3 }}
                    >
                      {item.label}
                      <Lock className="w-3 h-3" />
                    </span>
                    <NavTooltip
                      text={item.lockTooltip || ''}
                      visible={hoveredItem === item.label}
                    />
                  </span>
                ) : (
                  <a
                    href={getHref(item)}
                    className="text-xs text-text-muted font-mono uppercase tracking-wider hover:text-electric-cyan transition-colors px-2 py-1"
                  >
                    {item.label}
                  </a>
                )}
              </span>
            );
          })}
          <span className="text-electric-cyan/40 text-[10px]">♪</span>
          <button
            onClick={handleLogout}
            title={isAdminViewing ? 'Back to Admin' : 'Sign Out'}
            className="p-2 text-text-muted hover:text-red-400 transition-colors rounded-lg hover:bg-white/5"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>

        {/* Mobile: sign-out icon + hamburger */}
        <div className="flex md:hidden items-center gap-1">
          <button
            onClick={handleLogout}
            title={isAdminViewing ? 'Back to Admin' : 'Sign Out'}
            className="p-2 text-text-muted hover:text-red-400 transition-colors rounded-lg hover:bg-white/5"
          >
            <LogOut className="w-4 h-4" />
          </button>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 text-text-secondary hover:text-white transition-colors rounded-lg hover:bg-white/5"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/5 bg-deep-charcoal/95 backdrop-blur-lg">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
            {NAV_ITEMS.map((item) => {
              const isActive = getKey(item) === currentPage;
              const locked = isItemLocked(item);

              if (isActive) {
                return (
                  <span
                    key={item.label}
                    className="text-sm text-white font-mono uppercase tracking-wider px-3 py-2.5 rounded-lg bg-white/5"
                  >
                    {item.label}
                  </span>
                );
              }

              if (locked) {
                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => setHoveredItem(item.label)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <span
                      className="text-sm text-text-muted font-mono uppercase tracking-wider px-3 py-2.5 rounded-lg flex items-center gap-2 cursor-not-allowed select-none"
                      style={{ opacity: 0.3 }}
                    >
                      {item.label}
                      <Lock className="w-3.5 h-3.5" />
                    </span>
                    {hoveredItem === item.label && item.lockTooltip && (
                      <div className="ml-3 mt-1 mb-1 px-3 py-2 bg-electric-cyan/5 border border-electric-cyan/20 rounded-lg">
                        <p className="text-[11px] text-electric-cyan/80 font-mono leading-relaxed">
                          {item.lockTooltip}
                        </p>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <a
                  key={item.label}
                  href={getHref(item)}
                  className="text-sm text-text-muted font-mono uppercase tracking-wider hover:text-electric-cyan transition-colors px-3 py-2.5 rounded-lg hover:bg-white/5"
                >
                  {item.label}
                </a>
              );
            })}
          </div>
        </div>
      )}
      {/* Admin impersonation banner */}
      {isAdminViewing && (
        <div className="border-t border-amber-500/20 bg-amber-500/10 backdrop-blur-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-8 flex items-center justify-between">
            <span className="text-xs text-amber-400 font-mono flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" />
              Admin viewing as: <strong>{adminAsSlug}</strong>
            </span>
            <a
              href="/admin"
              className="text-xs text-amber-400 hover:text-amber-300 font-mono transition-colors"
              onClick={() => clearAdminAsSlug()}
            >
              ← Back to Admin
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}
